'use client';

import { DeleteOutlined, Edit as EditIcon } from '@mui/icons-material';
import { Button, Dialog, Grid } from '@mui/material';
import { DataGridPremium, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import { gql, useLazyQuery, useMutation } from '@parkchirp/api-client';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../../contexts';
import { SetShowSnackbar } from '../../../types';
import { dayjs, isAdminDashboard, isOwnerPortal, isTenantPortal } from '../../../utils';
import { EditDefaultRateFeeHistoryModal } from './editDefaultRateFeeHistoryModal';
import { EditRateHistoryModal } from './editRateHistoryModal';
import { getHistoryErrorMessage } from './shared';

type RateHistoryProps = {
	parkerId: string;
	terminationStatus: string;
	billingDay: number | null;
	parkerLoaded: boolean;
	parkerHasBeenInvoiced: (date: string) => boolean;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	isEditMode: boolean;
	refetchParkersFromCache?: () => void;
	usesDefaultRates: boolean;
	fetchParkerSublotHistory: () => Promise<void>;
	deleteParkerSublotHistory: (args) => void;
	sublotHistory: any[];
	updateParkerSublotHistory: (args) => void;
	facilityId: string;
};

const GET_PARKER_RATE_HISTORY = gql`
    query getParkerRateHistory(
        $parkerId: String!,
        $tenantSubscriptionId: String
    ) {
        getParkerRateHistory(
            parkerId: $parkerId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            id,
            startDate,
            rate {
                id
                name
                parentRateId
            }
        }
    }
`;

const GET_PARKER_DEFAULT_RATE_FEE_HISTORY = gql`
    query getParkerDefaultRateFeeHistory(
        $parkerId: String!,
        $tenantSubscriptionId: String,
        $facilityId: String,
    ) {
        getParkerDefaultRateFeeHistory(
            parkerId: $parkerId,
            tenantSubscriptionId: $tenantSubscriptionId
            facilityId: $facilityId
        ) {
            id
            price
            notes
            startDate
            inactiveDate
            productCodeId
            rateCodeId
            feeId
            tax1TaxTableId
            tax2TaxTableId
            tax3TaxTableId
            tax4TaxTableId
            tax1
            tax2
            tax3
            tax4
            taxCalculation
            hasBeenUsed
            changeReason
        }
    }
`;

const DELETE_PARKER_RATE_HISTORY = gql`
    mutation deleteParkerRateHistory(
        $parkerRateHistoryId: Int!,
        $tenantSubscriptionId: String
    ) {
        deleteParkerRateHistory(
            parkerRateHistoryId: $parkerRateHistoryId,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const DELETE_PARKER_FEE_HISTORY = gql`
    mutation deleteParkerFeeHistory(
        $parkerId: String!
        $feeHistoryId: String!
        $tenantSubscriptionId: String
        $facilityId: String,
    ) {
        deleteParkerFeeHistory(
            parkerId: $parkerId,
            feeHistoryId: $feeHistoryId
            tenantSubscriptionId: $tenantSubscriptionId
            facilityId: $facilityId
        )
    }
`;

export const RateHistoryTable = ({
	parkerId,
	terminationStatus,
	billingDay,
	parkerLoaded,
	parkerHasBeenInvoiced,
	setShowSnackbar,
	tenantSubscriptionId,
	isEditMode,
	refetchParkersFromCache,
	usesDefaultRates,
	fetchParkerSublotHistory,
	deleteParkerSublotHistory,
	sublotHistory,
	updateParkerSublotHistory,
	facilityId,
}: RateHistoryProps) => {
	const [rateHistory, setRateHistory] = useState([]);
	const [showEditRateHistoryModal, setShowEditRateHistoryModal] = useState<boolean>(false);
	const [showEditDefaultRateFeeHistoryModal, setShowEditDefaultRateFeeHistoryModal] = useState<boolean>(false);
	const [rateHistoryIdToEdit, setRateHistoryIdToEdit] = useState(null);
	const [getParkerRateHistory, { loading: rateHistoryLoading }] = useLazyQuery(GET_PARKER_RATE_HISTORY);
	const [getParkerDefaultRateFeeHistory, { loading: defaultRateFeeHistoryLoading }] = useLazyQuery(
		GET_PARKER_DEFAULT_RATE_FEE_HISTORY,
	);
	const [deleteParkerRateHistory, { loading: deleteInProgress }] = useMutation(DELETE_PARKER_RATE_HISTORY);
	const [deleteParkerFeeHistory, { loading: deleteFHInProgress }] = useMutation(DELETE_PARKER_FEE_HISTORY);
	const [feesHistory, setFeesHistory] = useState([]);
	const [feeHistoryIdToEdit, setFeeHistoryIdToEdit] = useState(null);
	const [activeFeeHistoryPrice, setActiveFeeHistoryPrice] = useState<string>(null);
	const { hasComponentAccess } = useContext(CommonContext);

	const parkerPendingOrNotInvoiced = ['NOT_INVOICED', 'PENDING'].includes(terminationStatus);
	const parkerTerminated = terminationStatus === 'TERMINATED';

	const fetchParkerRateHistory = useCallback(async () => {
		const { data: parkerRateHistoryData } = await getParkerRateHistory({
			variables: { parkerId, tenantSubscriptionId },
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
		});

		const parkerRateHistory = parkerRateHistoryData.getParkerRateHistory.map((rateHistoryRecord) => ({
			id: rateHistoryRecord.id,
			startDate: rateHistoryRecord.startDate,
			rateId: rateHistoryRecord.rate.id,
			rateName: rateHistoryRecord.rate.name,
			parentRateId: rateHistoryRecord.rate.parentRateId,
		}));

		setRateHistory(cloneDeep(parkerRateHistory));
	}, [getParkerRateHistory, parkerId, tenantSubscriptionId]);

	const fetchParkerFeeHistory = useCallback(async () => {
		await getParkerDefaultRateFeeHistory({
			variables: {
				parkerId,
				tenantSubscriptionId,
				facilityId,
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
			onCompleted: (data) => {
				const feeHistoryData = data.getParkerDefaultRateFeeHistory.map(
					({ price, startDate, inactiveDate, ...r }) => ({
						price: (price / 100).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						}),
						startDate: dayjs.utc(startDate).format('MM/DD/YYYY'),
						inactiveDate: inactiveDate ? dayjs.utc(inactiveDate).format('MM/DD/YYYY') : null,
						...r,
					}),
				);
				const today = dayjs.utc();
				setFeesHistory(feeHistoryData);
				const currentFeeHistory = feeHistoryData.find((fh) => {
					const start = dayjs.utc(fh.startDate, 'MM/DD/YYYY');
					const inactive = fh.inactiveDate ? dayjs.utc(fh.inactiveDate, 'MM/DD/YYYY') : null;

					const startOK = start.isSameOrBefore(today);
					const inactiveOK = inactive === null || inactive.isSameOrAfter(today);

					return startOK && inactiveOK;
				});
				if (currentFeeHistory) {
					setActiveFeeHistoryPrice(currentFeeHistory.price);
				}
			},
		});
	}, [parkerId, tenantSubscriptionId, getParkerDefaultRateFeeHistory, facilityId]);

	useEffect(() => {
		if (!usesDefaultRates) {
			fetchParkerRateHistory();
		}
		if (usesDefaultRates) {
			fetchParkerFeeHistory();
		}
	}, [fetchParkerRateHistory, fetchParkerFeeHistory, usesDefaultRates]);

	const handleChangeRateClick = useCallback((setStateFn) => {
		setStateFn(true);
	}, []);

	const handleEditClick = useCallback(
		(historyId: number, setStateFn) => {
			setStateFn(historyId);
			if (usesDefaultRates) {
				setShowEditDefaultRateFeeHistoryModal(true);
			} else {
				setShowEditRateHistoryModal(true);
			}
		},
		[usesDefaultRates],
	);

	const handleDeleteClick = useCallback(
		async (parkerRateHistoryId: number) => {
			try {
				await deleteParkerRateHistory({
					variables: { parkerRateHistoryId, tenantSubscriptionId },
					context: { apiVersion: 'v2' },
				});
				if (isTenantPortal) {
					const { startDate: startDateFromRateHistory } = rateHistory.find(
						(rate) => rate.id === parkerRateHistoryId,
					);
					const correspondingSublot = sublotHistory.find(
						(sublot) => sublot.startDate === startDateFromRateHistory,
					);
					if (!correspondingSublot) return;
					await deleteParkerSublotHistory({
						variables: {
							parkerSublotHistoryId: correspondingSublot.id,
							tenantSubscriptionId,
						},
						context: { apiVersion: 'v2' },
					});
				}

				await fetchParkerRateHistory();
				await fetchParkerSublotHistory();
			} catch (err) {
				setShowSnackbar({
					message: getHistoryErrorMessage(err, 'Failed to delete scheduled rate change'),
					severity: 'error',
					autoHideDuration: 6000,
				});
			}
		},
		[
			deleteParkerRateHistory,
			tenantSubscriptionId,
			fetchParkerRateHistory,
			fetchParkerSublotHistory,
			rateHistory,
			sublotHistory,
			deleteParkerSublotHistory,
			setShowSnackbar,
		],
	);

	const handleDeleteFeeHistoryClick = useCallback(
		async (feeHistoryId: number) => {
			try {
				await deleteParkerFeeHistory({
					variables: { feeHistoryId, parkerId, tenantSubscriptionId },
					context: { apiVersion: 'v2' },
				});

				await fetchParkerFeeHistory();
			} catch (err) {
				setShowSnackbar({
					message: getHistoryErrorMessage(err, 'Failed to delete scheduled rate change'),
					severity: 'error',
					autoHideDuration: 6000,
				});
			}
		},
		[fetchParkerFeeHistory, setShowSnackbar, deleteParkerFeeHistory, parkerId, tenantSubscriptionId],
	);

	const closeModal = useCallback(async () => {
		setShowEditRateHistoryModal(false);
		setShowEditDefaultRateFeeHistoryModal(false);
		setRateHistoryIdToEdit(null);
		setFeeHistoryIdToEdit(null);

		if (usesDefaultRates) {
			await fetchParkerFeeHistory();
		} else {
			await fetchParkerRateHistory();
		}
	}, [fetchParkerRateHistory, fetchParkerFeeHistory, usesDefaultRates]);

	const displayActionIcon = useCallback(
		(row, isDelete = false) => {
			if (isDelete && row.id === rateHistory[0]?.id) return false;
			return !parkerHasBeenInvoiced(row.startDate);
		},
		[parkerHasBeenInvoiced, rateHistory],
	);

	const isFutureDate = (date: string | Date) => dayjs.utc(date).isAfter(dayjs());

	const displayActionIconForFeeHistory = useCallback(
		(row, isDelete = false) => {
			if (isDelete && row.id === feesHistory[0]?.id) return false;

			const isLastItem = row.id === feesHistory[feesHistory.length - 1]?.id;
			return isLastItem && isFutureDate(row.startDate);
		},
		[feesHistory],
	);

	const columns: GridColDef[] = [
		{
			field: 'rateName',
			headerName: 'Rate',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 3,
			resizable: false,
			sortable: false,
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			headerAlign: 'center' as const,
			align: 'center' as const,
			renderCell: (params) => dayjs.utc(params.row.startDate).format('MM/DD/YYYY'),
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'parentRateId',
			headerName: 'Parent or Child',
			headerAlign: 'center' as const,
			align: 'center' as const,
			renderCell: (params) => {
				if (params.row.parentRateId) {
					return 'Child';
				}
				return 'Parent';
			},
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'actions',
			type: 'actions' as const,
			getActions: (params) => {
				const rateHistoryId = params.row.id;

				return [
					...(displayActionIcon(params.row) && !isEditMode
						? [
								<GridActionsCellItem
									key={`edit-${rateHistoryId}`}
									icon={<EditIcon />}
									label='Edit'
									onClick={() => handleEditClick(rateHistoryId, setRateHistoryIdToEdit)}
								/>,
							]
						: []),
					...(displayActionIcon(params.row, true) && !isEditMode
						? [
								<GridActionsCellItem
									key={`delete-${rateHistoryId}`}
									icon={<DeleteOutlined />}
									label='Delete'
									onClick={() => handleDeleteClick(rateHistoryId)}
								/>,
							]
						: []),
				];
			},
			flex: 1,
			resizable: false,
			sortable: false,
		},
	];

	const editRateHistoryDialog = (
		<Dialog open={showEditRateHistoryModal}>
			<Grid item p={3}>
				<EditRateHistoryModal
					parkerId={parkerId}
					rateHistoryId={rateHistoryIdToEdit}
					rateHistory={rateHistory}
					parkerHasBeenInvoiced={parkerHasBeenInvoiced}
					closeModal={closeModal}
					setShowSnackbar={setShowSnackbar}
					tenantSubscriptionId={tenantSubscriptionId}
					refetchParkersFromCache={refetchParkersFromCache}
					fetchParkerSublotHistory={fetchParkerSublotHistory}
					sublotHistory={sublotHistory}
					updateParkerSublotHistory={updateParkerSublotHistory}
				/>
			</Grid>
		</Dialog>
	);

	const defaultRateFeeHistoryColumns: GridColDef[] = [
		{
			field: 'actions',
			type: 'actions' as const,
			headerName: 'Actions',
			minWidth: 80,
			getActions: (params) => {
				const feeHistoryId = params.row.id;
				if (params.row.hasBeenUsed) {
					return [];
				}

				return [
					...(displayActionIconForFeeHistory(params.row) && !isEditMode
						? [
								<GridActionsCellItem
									key={`edit-${feeHistoryId}`}
									icon={<EditIcon />}
									label='Edit'
									onClick={() => handleEditClick(feeHistoryId, setFeeHistoryIdToEdit)}
								/>,
							]
						: []),
					...(displayActionIconForFeeHistory(params.row, true) && !isEditMode
						? [
								<GridActionsCellItem
									key={`delete-${feeHistoryId}`}
									icon={<DeleteOutlined />}
									label='Delete'
									onClick={() => handleDeleteFeeHistoryClick(feeHistoryId)}
								/>,
							]
						: []),
				];
			},
			flex: 1,
			resizable: false,
			sortable: false,
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'inactiveDate',
			headerName: 'End Date',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'price',
			headerName: 'Price',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'notes',
			headerName: 'Notes',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'changeReason',
			type: 'singleSelect' as const,
			valueOptions: [
				{ value: 'NEGOTIATED', label: 'Negotiated' },
				{ value: 'NO_REASON', label: 'No Reason' },
				{ value: 'POSTPONED', label: 'Postponed' },
				{ value: 'REDUCED', label: 'Reduced' },
				{ value: 'RESCINDED', label: 'Rescinded' },
			],
			headerName: 'Reason',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 2,
			resizable: false,
			sortable: false,
		},
	];

	const editDefaultRateFeeHistoryDialog = (
		<Dialog open={showEditDefaultRateFeeHistoryModal}>
			<Grid item p={3}>
				<EditDefaultRateFeeHistoryModal
					parkerId={parkerId}
					closeModal={closeModal}
					feeHistoryId={feeHistoryIdToEdit}
					feesHistory={feesHistory}
					setShowSnackbar={setShowSnackbar}
					tenantSubscriptionId={tenantSubscriptionId}
					facilityId={facilityId}
					setShowEditDefaultRateFeeHistoryModal={setShowEditDefaultRateFeeHistoryModal}
					fetchParkerFeeHistory={fetchParkerFeeHistory}
					activeFeeHistoryPrice={activeFeeHistoryPrice}
				/>
			</Grid>
		</Dialog>
	);

	const loading =
		rateHistoryLoading ||
		!billingDay ||
		deleteInProgress ||
		!parkerLoaded ||
		deleteFHInProgress ||
		defaultRateFeeHistoryLoading;

	if (usesDefaultRates) {
		return showEditDefaultRateFeeHistoryModal ? (
			editDefaultRateFeeHistoryDialog
		) : (
			<Grid container>
				<Grid item xs={12}>
					<DataGridPremium
						rows={feesHistory}
						columns={defaultRateFeeHistoryColumns}
						loading={loading}
						autoHeight
						disableColumnReorder
						disableColumnMenu
						hideFooter
					/>
				</Grid>
				{(!isAdminDashboard || hasComponentAccess('ParkerDetailModalScheduleRateChangeButton')) && (
					<Grid pt={2} item xs={12}>
						{!isEditMode && !isOwnerPortal ? (
							<Button
								variant='contained'
								onClick={() => handleChangeRateClick(setShowEditDefaultRateFeeHistoryModal)}
								size='small'
								disabled={parkerPendingOrNotInvoiced || parkerTerminated}
								data-cy='schedule-rate-change-button'
							>
								Schedule Rate Change
							</Button>
						) : null}
					</Grid>
				)}
			</Grid>
		);
	}

	return showEditRateHistoryModal ? (
		editRateHistoryDialog
	) : (
		<>
			<DataGridPremium
				rows={rateHistory}
				columns={columns}
				loading={loading}
				autoHeight
				disableColumnReorder
				disableColumnMenu
				hideFooter
			/>
			{(!isAdminDashboard || hasComponentAccess('ParkerDetailModalScheduleRateChangeButton')) && (
				<Grid item pt={2}>
					{!isEditMode && !isOwnerPortal ? (
						<Button
							variant='contained'
							onClick={() => handleChangeRateClick(setShowEditRateHistoryModal)}
							size='small'
							disabled={parkerPendingOrNotInvoiced || parkerTerminated}
							data-cy='schedule-rate-change-button'
						>
							Schedule Rate Change
						</Button>
					) : null}
				</Grid>
			)}
		</>
	);
};
